// import { faSquareFacebook, faSquareInstagram, faSquareXTwitter } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, {
    useCallback, useEffect, useState, useContext,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { faGlobe } from '@fortawesome/pro-regular-svg-icons';
import * as routes from '../../../constants/routes';
import { LISTING_TYPE_ORGANIZATION, LISTING_TYPE_SPONSOR } from '../../../constants/site';
import {
    COLORS, SIZES,
    POL_THEME,
    ThemeContext,
} from '../../../constants/theme';
import { loadSicCodes } from '../../../services/listing';
import ContactType from '../../../types/ContactType';
import { formatPostalCode } from '../../../utils/location';
import Button from '../Button';
import Divider from '../Divider';
import { QUERY_STRING_FIELDS } from '../SearchForm';
import styles from './DetailPageHeader.scss';
import { withHttp } from '../../../utils/url';

const DetailPageHeader = ({
    contactInfo,
    categories,
    categoriesAreSicCodes,
    classes,
    pageTitle,
    pageSlug,
    listingType,
    currentVariant,
    websiteUrl,
    emailDate,
}) => {
    const [sicCodes, setSicCodes] = useState(null);
    const themeContext = useContext(ThemeContext);

    const loadData = useCallback(async () => {
        if (categoriesAreSicCodes) {
            await loadSicCodes(setSicCodes, () => { });
        }
    }, [categoriesAreSicCodes]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const getCategoryName = (category) => {
        if (!categoriesAreSicCodes || !sicCodes) {
            return category;
        }

        return sicCodes[category];
    };

    const history = useNavigate();

    const goToSearch = (category) => {
        const params = new URLSearchParams();

        params.set(QUERY_STRING_FIELDS.featuresStreaming, 'false');
        params.set(QUERY_STRING_FIELDS.searchLocation, formatPostalCode(contactInfo.listingZip));
        params.set(QUERY_STRING_FIELDS.searchName, '');
        params.set(QUERY_STRING_FIELDS.searchCategory, category);
        params.set(QUERY_STRING_FIELDS.searchType, listingType);

        history(`${routes.SEARCH}?${params.toString()}`, { fromDashboard: true });
    };

    return (
        <div className={classNames(classes.root, styles.container)}>
            <h1
                className={classNames(classes.title, styles.title)}
                {...(currentVariant === 'email-content' && {
                    'aria-label': `${pageTitle} Email Content for ${emailDate}`,
                })}
            >
                {currentVariant !== 'default' ? (
                    <Link to={`/organization/${pageSlug}`}>
                        ‹
                        {' '}
                        {pageTitle}
                    </Link>
                ) : pageTitle}
                {currentVariant === 'subscribe' && <strong> / Subscribe</strong>}
                {currentVariant === 'unsubscribe' && <strong> / Unsubscribe</strong>}
            </h1>
            <div className={styles.siteInfoContainer}>
                {(categories
                  && (listingType === LISTING_TYPE_SPONSOR
                  || (listingType === LISTING_TYPE_ORGANIZATION && themeContext !== POL_THEME)))
                    && (
                        <div className={styles.categoriesContainer}>
                            {categories.length > 1 ? 'Categories ' : 'Category '}
                            <div>
                                {categories.map((category, i) => (
                                    <div
                                        key={`${category}-${i}`} // eslint-disable-line react/no-array-index-key
                                        className={styles.categoryButtonContainer}
                                    >
                                        <Button
                                            size={SIZES.xsmall}
                                            color={COLORS.flat}
                                            onClick={() => goToSearch(category)}
                                        >
                                            <span>{getCategoryName(category)}</span>
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                <div className={styles.shareContainer}>
                    {websiteUrl?.trim() && (
                        <a
                            href={withHttp(websiteUrl.trim())}
                            rel="noreferrer"
                            target="_blank"
                            className={styles.fullWidth}
                            aria-label="Organization Website"
                        >
                            <div className={styles.inlineContainer}>
                                <FontAwesomeIcon
                                    className={styles.visitWebsiteIcon}
                                    icon={faGlobe}
                                />
                                <span className={styles.visitWebsiteText}>
                                    Visit Website
                                </span>
                            </div>
                        </a>
                    )}
                    {/* This code is commented out at the request of  Mr. Manager, as Marketing is still finalizing designs. */}

                    {/* <div>Share</div>
                    <a
                        href={twitterShareLink}
                        rel="noreferrer"
                        target="_blank"
                        aria-label="X"
                    >
                        <FontAwesomeIcon icon={faSquareXTwitter} />
                    </a>
                    <a
                        href={facebookShareLink}
                        rel="noreferrer"
                        target="_blank"
                        aria-label="Facebook"
                    >
                        <FontAwesomeIcon icon={faSquareFacebook} />
                    </a>
                    <a
                        href="https://www.instagram.com/"
                        rel="noreferrer"
                        target="_blank"
                        aria-label="Instagram"
                    >
                        <FontAwesomeIcon icon={faSquareInstagram} />
                    </a> */}

                </div>

            </div>
            <Divider classes={{ root: styles.divider }} />
        </div>
    );
};

DetailPageHeader.propTypes = {
    contactInfo: ContactType.isRequired,
    currentVariant: PropTypes.string,
    categories: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.arrayOf(PropTypes.number)]),
    categoriesAreSicCodes: PropTypes.bool,
    classes: PropTypes.shape({
        root: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
        title: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
    }),
    pageTitle: PropTypes.string,
    pageSlug: PropTypes.string,
    listingType: PropTypes.oneOf([LISTING_TYPE_ORGANIZATION, LISTING_TYPE_SPONSOR]).isRequired,
    websiteUrl: PropTypes.string,
    emailDate: PropTypes.string,
};

DetailPageHeader.defaultProps = {
    categories: null,
    categoriesAreSicCodes: false,
    classes: {
        root: null,
        title: null,
    },
    pageSlug: '',
    pageTitle: '',
    currentVariant: 'default',
    websiteUrl: null,
    emailDate: null,
};

export default DetailPageHeader;
